import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import Spinner from 'react-bootstrap/Spinner';
import Form from 'react-bootstrap/Form';

import { URLS } from '../../resources/constants';
import { useForecastApi } from '../../resources/hooks/api/useForecastApiHook';
import { pctText, useRatioText } from './KeyRatios';
import ForecastLayout from './ForecastLayout';
import { GroupedBarChart, HorizontalBars, LineChart, compactMoney } from './ForecastCharts';
import { MONTH_FULL_NAMES } from './forecastUtils';

/**
 * Executive Dashboard: one-page summary of the calculated forecast.
 *   - KPI cards for the selected year
 *   - Annual operating forecast, Year 1-5 (grouped bars)
 *   - Cash on hand by year (year-end balances)
 *   - Monthly financial view of the selected year
 *   - Largest expense lines of the selected year
 */
const ExecutiveDashboard: React.FC = () => {
    const { t } = useTranslation('scenes.FiveYearForecast');
    const navigate = useNavigate();
    const { forecastId } = useParams();
    const id = Number(forecastId);
    const { getResults } = useForecastApi();

    const [results, setResults] = useState<any>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [year, setYear] = useState<number>(1);

    useEffect(() => {
        if (!id) return;
        setLoading(true);
        getResults(id, (_m: string, resp: any) => {
            setResults(resp?.data?.data);
            setLoading(false);
        }, (msg: string) => {
            toast.error(msg);
            setLoading(false);
        });
    }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    const S = results?.summary || {};
    const yearIdx = year - 1;
    const tr = (key: string, fallback: string) => String(t(key, fallback));
    const ratioText = useRatioText();

    const topExpenses = useMemo(() => (results?.lines || [])
        .filter((l: any) => l.category === 'operating_expense' || l.category === 'payroll')
        .map((l: any) => ({ label: l.description ? `${l.name} / ${l.description}` : l.name, value: l.yearly[yearIdx] }))
        .filter((i: any) => i.value > 0)
        .sort((a: any, b: any) => b.value - a.value)
        .slice(0, 7), [results, yearIdx]);

    if (loading || !results) {
        return (
            <ForecastLayout active="executive_dashboard" forecastId={id}>
                <div className="fyf-empty"><Spinner animation="border" /></div>
            </ForecastLayout>
        );
    }

    const f = results.forecast;
    /** Calendar year a forecast year starts in, e.g. 2027 for a Year 1 starting Oct 2027. */
    const yearLabel = (n: number): string =>
        String(Number(f.start_year) + Math.floor((Number(f.start_month) - 1 + (n - 1) * 12) / 12));
    const yearLabels = [1, 2, 3, 4, 5].map(yearLabel);
    const y = (key: string) => S[key]?.yearly?.[yearIdx] ?? 0;
    // Five headline cards for the selected year; each shows its key ratio underneath.
    // Break-even is one date for the whole forecast, so it sits in a summary line above.
    const R = results.ratios;
    const cash = results.cash.ending.yearly[yearIdx];
    type Card = { key: string; name: string; value: string; neg?: boolean; sub?: string };
    const money = (v: number) => compactMoney(v);
    const cards: Card[] = [
        { key: 'rev', name: tr('dashboard.revenue', 'Revenue'), value: money(y('gross_revenue')),
          sub: R && year > 1 ? `${pctText(R.revenue_growth[yearIdx])} ${tr('dashboard.vsPrior', 'vs prior year')}` : undefined },
        { key: 'gp', name: tr('results.grossProfit', 'Gross Profit'), value: money(y('gross_profit')), neg: y('gross_profit') < 0,
          sub: R ? `${pctText(R.gross_margin[yearIdx])} ${tr('dashboard.margin', 'margin')}` : undefined },
        { key: 'ebitda', name: 'EBITDA', value: money(y('ebitda')), neg: y('ebitda') < 0,
          sub: R ? `${pctText(R.ebitda_margin[yearIdx])} ${tr('dashboard.margin', 'margin')}` : undefined },
        { key: 'ni', name: tr('results.netIncome', 'Net Income'), value: money(y('net_income')), neg: y('net_income') < 0,
          sub: R ? `${pctText(R.net_margin[yearIdx])} ${tr('dashboard.margin', 'margin')}` : undefined },
        { key: 'cash', name: tr('dashboard.cashYearEnd', 'Cash at Year End'), value: money(cash), neg: cash < 0,
          sub: R ? `${tr('ratios.runwayShort', 'Cash runway')}: ${ratioText.runway(R.cash_runway_months[yearIdx])}` : undefined },
    ];
    const monthLabels = results.months.slice(yearIdx * 12, year * 12)
        .map((m: any) => m.label.split(' ')[0].charAt(0));
    const monthSlice = (key: string) => S[key].monthly.slice(yearIdx * 12, year * 12);
    const expensesMonthly = monthSlice('payroll').map((v: number, i: number) => v + monthSlice('operating_expenses')[i]);
    const empty = (results.lines || []).length === 0;

    return (
        <ForecastLayout active="executive_dashboard" forecastId={id}>
            <div className="d-flex justify-content-between align-items-start gap-3">
                <div className="flex-grow-1" style={{ minWidth: 0 }}>
                    <button type="button" className="fyf-back" onClick={() => navigate(URLS.FIVE_YEAR_FORECAST)}>
                        ← {t('entry.back', 'All forecasts')}
                    </button>
                    <h1>{t('nav.executive_dashboard', 'Executive Dashboard')}</h1>
                    <div className="fyf-sub mb-1">{t('dashboard.subtitle', 'Five-year forecast summary')}</div>
                    <div className="fyf-meta">
                        <strong className="text-dark text-break">{f.name}</strong>
                        {' · '}{t('entry.starts', 'Starts {{month}} {{year}}', { month: MONTH_FULL_NAMES[f.start_month - 1], year: f.start_year })}
                    </div>
                </div>
                <div className="fyf-yearpick flex-shrink-0">
                    <Form.Label htmlFor="fyf-year">{t('dashboard.selectedYear', 'Selected Year')}</Form.Label>
                    <Form.Select id="fyf-year" className="fyf-edit" value={year} onChange={(e) => setYear(Number(e.target.value))}>
                        {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{yearLabel(n)}</option>)}
                    </Form.Select>
                </div>
            </div>

            {empty ? (
                <div className="fyf-panel fyf-empty mt-3">
                    <p>{t('results.noLines', 'No forecast entries yet. Add accounts on the Forecast Entry screen.')}</p>
                    <button type="button" className="fyf-primary"
                        onClick={() => navigate(URLS.FIVE_YEAR_FORECAST_ENTRY.replace(':forecastId', String(id)))}>
                        {t('results.editEntries', 'Edit Entries')}
                    </button>
                </div>
            ) : (
                <>
                    {R && (
                        <div className="fyf-summary-line mt-3">
                            <span>
                                <b>{tr('ratios.breakevenShort', 'Break-even month')}:</b>{' '}
                                {R.breakeven_month ? R.breakeven_month.label : tr('ratios.notReached', 'Not reached in 5 years')}
                                {R.breakeven_dips_month && (
                                    <span className="fyf-muted">
                                        {' '}{String(t('ratios.dipsAgain', '(dips again in {{month}})', { month: R.breakeven_dips_month.label }))}
                                    </span>
                                )}
                            </span>
                            <span className="fyf-sep">·</span>
                            <span><b>{tr('ratios.recoveredShort', 'Losses recovered')}:</b> {ratioText.recovered(R)}</span>
                            <span className="fyf-muted">{tr('dashboard.wholeForecast', '(whole 5-year forecast)')}</span>
                        </div>
                    )}

                    <div className="fyf-kpis fyf-kpis-5 mt-3">
                        {cards.map((c) => (
                            <div className="fyf-kpi" key={c.key}>
                                <div className="k">{c.name}</div>
                                <div className={`v${c.neg ? ' neg' : ''}`} title={c.value}>{c.value}</div>
                                {c.sub && <div className="s">{c.sub}</div>}
                            </div>
                        ))}
                    </div>

                    <div className="fyf-dash-grid">
                        <div className="fyf-card">
                            <div className="fyf-card-h">{t('dashboard.annualForecast', 'Annual Operating Forecast — Five Years')}</div>
                            <div className="fyf-card-b">
                                <GroupedBarChart
                                    title="annual-forecast"
                                    categories={yearLabels}
                                    series={[
                                        { name: tr('dashboard.income', 'Income'), values: S.gross_revenue.yearly },
                                        { name: tr('dashboard.cogs', 'COGS'), values: S.cogs.yearly },
                                        { name: tr('dashboard.payroll', 'Payroll'), values: S.payroll.yearly },
                                        { name: tr('dashboard.operatingExpenses', 'Operating Expenses'), values: S.operating_expenses.yearly },
                                        { name: 'EBITDA', values: S.ebitda.yearly },
                                    ]}
                                />
                            </div>
                        </div>
                        <div className="fyf-card">
                            <div className="fyf-card-h">{t('dashboard.cashByYear', 'Cash on Hand by Year')}</div>
                            <div className="fyf-card-b">
                                <LineChart
                                    title="cash-by-year"
                                    labels={yearLabels}
                                    series={[{ name: tr('results.cashOnHand', 'Cash on Hand'), values: results.cash.ending.yearly }]}
                                />
                            </div>
                        </div>
                        <div className="fyf-card">
                            <div className="fyf-card-h">
                                {t('dashboard.monthlyView', 'Monthly Financial View — Year {{n}}', { n: yearLabel(year) })}
                            </div>
                            <div className="fyf-card-b">
                                <LineChart
                                    title="monthly-view"
                                    labels={monthLabels}
                                    series={[
                                        { name: tr('dashboard.income', 'Income'), values: monthSlice('gross_revenue') },
                                        { name: tr('results.grossProfit', 'Gross Profit'), values: monthSlice('gross_profit') },
                                        { name: tr('dashboard.expenses', 'Payroll + Operating Expenses'), values: expensesMonthly },
                                        { name: tr('results.netIncome', 'Net Income'), values: monthSlice('net_income') },
                                    ]}
                                />
                            </div>
                        </div>
                        <div className="fyf-card">
                            <div className="fyf-card-h">
                                {t('dashboard.topExpenses', 'Operating Expense Detail — Top Categories (Year {{n}})', { n: year })}
                            </div>
                            <div className="fyf-card-b">
                                <HorizontalBars items={topExpenses}
                                    emptyText={tr('dashboard.noExpenses', 'No payroll or operating expenses in this year.')} />
                            </div>
                        </div>
                    </div>

                    {(results.warnings || []).length > 0 && (
                        <div className="fyf-note">{results.warnings.join(' ')}</div>
                    )}
                </>
            )}
        </ForecastLayout>
    );
};

export default ExecutiveDashboard;
